import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injectable, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { OffersComponent } from './features/admin/offers/offers.component';
import { MatButtonModule } from '@angular/material/button';
import { OfferDetailsComponent } from './features/admin/offers/offer-details/offer-details.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { PagesComponent } from './features/admin/pages/pages.component';
import { AutocompleteComponentComponent } from './shared/components/autocomplete-component/autocomplete-component.component';
import { MatChip, MatChipListbox, MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { RolesComponent } from './features/admin/pages/page-config/roles/roles.component';
import { AddComponentModalComponent } from './shared/components/add-component-modal/add-component-modal.component';
import { AddOfferModalComponent } from './shared/components/add-offer-modal/add-offer-modal.component';
import { EditableInputComponent } from './shared/components/editable-input/editable-input.component';
import { AddConditionModalComponent } from './shared/components/add-condition-modal/add-condition-modal.component';
import { ConditionComponent } from './features/admin/pages/page-config/condition/condition.component';
import { TranslationPipe } from './pipes/translation/translation.pipe';
import { AdminLayoutComponent } from './layout/admin-layout/admin-layout.component';
import { CustomerLayoutComponent } from './layout/customer-layout/customer-layout.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CustomMatPaginatorIntl } from './shared/components/custom-mat-paginator-int/custom-mat-paginator-int';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, Configuration, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from './services/environment/environment';
import { JwtInterceptor } from './interceptors/jwt-interceptor';
import { AuthComponent } from './features/auth/auth.component';
import { EmptyComponent } from './layout/empty/empty.component';
import { AuthGuard } from './interceptors/can-activate-interceptor';
import { PageFormComponent } from './features/customer/page-form/page-form.component';
import { BlockFormComponent } from './features/customer/page-form/block-form/block-form.component';
import { GroupFormComponent } from './features/customer/page-form/block-form/group-form/group-form.component';
import { ComponentFormComponent } from './features/customer/page-form/block-form/group-form/component-form/component-form.component';
import { PageConfigComponent } from './features/admin/pages/page-config/page-config.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BlockConfigComponent } from './features/admin/pages/page-config/block-config/block-config.component';
import { GroupConfigComponent } from './features/admin/pages/page-config/block-config/group-config/group-config.component';
import { ComponentConfigComponent } from './features/admin/pages/page-config/block-config/group-config/component-config/component-config.component';
import { MultipleConfigComponent } from './shared/components/multiple-config/multiple-config.component';
import { DndModule } from 'ngx-drag-drop';
import { SnackbarComponent } from './shared/components/snackbar/snackbar.component';
import { PageResolverComponent } from './features/page-resolver/page-resolver.component';
import { PreviewChooseModalComponent } from './shared/components/preview-choose-modal/preview-choose-modal.component';
import { ConnectionComponent } from './features/admin/pages/page-config/connection/connection.component';
import { AssociatePageModalComponent } from './shared/components/associate-page-modal/associate-page-modal.component';
import { AddPageModalComponent } from './shared/components/add-page-modal/add-page-modal.component';
import { LogoutComponent } from './features/logout/logout.component';
import { TooltipTruncateDirective } from './directives/matTooltip-text-truncate.directive';
import { NgxColorsModule } from 'ngx-colors';
import { InstructionsModalComponent } from './shared/components/instructions-modal/instructions-modal.component';
import { InsertTextDialogComponent } from './shared/components/insert-text-dialog/insert-text-dialog.component';
import { PageConfigElemMenu } from './features/admin/pages/page-config/page-config-elem-menu/page-config-elem-menu.component';
import { TagsModalComponent } from './shared/components/tags-modal/tags-modal.component';
import { PageHistoryModalComponent } from './shared/components/page-history-modal/page-history-modal.component';
import { PagesListComponent } from './features/customer/page-form/pages-list/pages-list.component';
import { SsoComponent } from './features/sso/sso.component';
import { SummaryModalComponent } from './shared/components/summary-modal/summary-modal.component';
import { EnvironmentService } from './services/environment/environment.service';
import { GenerateCustomerOfferModal } from './shared/components/generate-customer-offer-modal/generate-customer-offer-modal.component';
import { LinksModalComponent } from './shared/components/links-modal/links-modal.component';
import { MultiActionModalComponent } from './shared/components/multi-action-modal/multi-action-modal.component';
import { AngularEditorModule } from '@wfpena/angular-wysiwyg';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CardSelectComponentComponent } from './shared/components/card-select-component/card-select-component.component';
import { EditOptionModalComponent } from './shared/components/edit-option-modal/edit-option-modal.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TippyDirective } from './directives/tippy/tippy.directive';
import { StatsComponent } from './features/admin/stats/stats.component';
import { DatePipe } from '@angular/common';

const GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0/me';


export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(GRAPH_ENDPOINT, environment.azureAd.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.azureAd.scopes
    },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    OffersComponent,
    OfferDetailsComponent,
    AddOfferModalComponent,
    PagesComponent,
    AutocompleteComponentComponent,
    ConfirmDialogComponent,
    RolesComponent,
    AddComponentModalComponent,
    EditableInputComponent,
    AddConditionModalComponent,
    AssociatePageModalComponent,
    ConditionComponent,
    TranslationPipe,
    AdminLayoutComponent,
    CustomerLayoutComponent,
    PageFormComponent,
    BlockFormComponent,
    GroupFormComponent,
    EmptyComponent,
    AuthComponent,
    ComponentFormComponent,
    PageConfigComponent,
    BlockConfigComponent,
    GroupConfigComponent,
    ComponentConfigComponent,
    MultipleConfigComponent,
    SnackbarComponent,
    PageResolverComponent,
    PreviewChooseModalComponent,
    ConnectionComponent,
    AddPageModalComponent,
    LogoutComponent,
    TooltipTruncateDirective,
    InstructionsModalComponent,
    InsertTextDialogComponent,
    PageConfigElemMenu,
    TagsModalComponent,
    PageHistoryModalComponent,
    PagesListComponent,
    SsoComponent,
    SummaryModalComponent,
    GenerateCustomerOfferModal,
    LinksModalComponent,
    MultiActionModalComponent,
    EditOptionModalComponent,
    TippyDirective,
    StatsComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatSnackBarModule,
    MatDialogModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatTooltip,
    MatExpansionModule,
    MatCheckboxModule,
    MatCardModule,
    MatSelectModule,
    MatTabsModule,
    MatRadioModule,
    MatGridListModule,
    MsalModule,
    MatStepperModule,
    FontAwesomeModule,
    DragDropModule,
    DndModule,
    MatMenuModule,
    NgxColorsModule,
    AngularEditorModule,
    NgxMaskDirective,
    CardSelectComponentComponent,
    MatSlideToggleModule
],
  providers: [
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: (env: EnvironmentService) => async () => {
        await env.loadMsalConfig(); // only load MSAL config and create PCA once
      },
      deps: [EnvironmentService],
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: (env: EnvironmentService) => env.pca, // already created in loadMsalConfig()
      deps: [EnvironmentService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    AuthGuard,
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntl
    },
    MsalService,
    MsalBroadcastService,
    provideNgxMask(),
    DatePipe
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
